import React, { useState, useEffect } from 'react';
import './SubjectInstructor.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { axiosInstance } from '../../../../../utils/tokenUtils';

const SubjectInstructor = ({ open, onClose, subjectType, totalInstructors }) => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && subjectType) {
      fetchSubjects();
    }
  }, [open, subjectType]);

  const fetchSubjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = 50;
      let allSubjects = [];
      let currentPage = 1;
      let hasMoreData = true;

      while (hasMoreData) {
        const response = await axiosInstance.get(
          `/admin/dashboard/subjects-with-instructor-count?subject_type=${subjectType}&page=${currentPage}&page_size=${pageSize}`
        );

        const subjectsData = response.data.results || response.data;
        allSubjects = [...allSubjects, ...subjectsData];

        // Check if we've fetched all subjects
        if (response.data.count && allSubjects.length >= response.data.count) {
          hasMoreData = false;
        } else if (subjectsData.length < pageSize) {
          // If we got fewer results than page size, we've reached the end
          hasMoreData = false;
        } else {
          currentPage++;
        }
      }

      setSubjects(allSubjects);
    } catch (err) {
      setError('Failed to load subjects');
      console.error('Error fetching subjects:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectTypeIcon = () => {
    return subjectType === 'academic' ? (
      <SchoolIcon className="subject-type-icon" />
    ) : (
      <PaletteIcon className="subject-type-icon" />
    );
  };

  const getSubjectTypeTitle = () => {
    return subjectType === 'academic' ? 'Academic Subjects' : 'Creative Arts';
  };

  const getSubjectTypeColor = () => {
    return subjectType === 'academic' ? '#2563EB' : '#7BCB8F';
  };

  // Sort subjects by instructor count (descending)
  const sortedSubjects = [...subjects].sort((a, b) => b.instructor_count - a.instructor_count);

  // Split subjects into two columns
  const midPoint = Math.ceil(sortedSubjects.length / 2);
  const leftColumn = sortedSubjects.slice(0, midPoint);
  const rightColumn = sortedSubjects.slice(midPoint);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="subject-instructor-modal"
      PaperProps={{
        className: 'subject-instructor-paper',
      }}
    >
      <DialogTitle className="subject-instructor-title">
        <Box className="subject-instructor-header">
          <Box className="subject-type-header">
            {getSubjectTypeIcon()}
            <Typography variant="h6" className="subject-type-title">
              {getSubjectTypeTitle()}
            </Typography>
          </Box>
          {/* <Box className="subject-instructor-summary">
            <Chip
              label={`${subjects.length} Subjects`}
              className="subject-count-chip"
              style={{
                backgroundColor: getSubjectTypeColor() + '20',
                color: getSubjectTypeColor(),
              }}
            />
            <Chip
              label={`${totalInstructors} Total Instructors`}
              className="instructor-count-chip"
              style={{
                backgroundColor: getSubjectTypeColor() + '20',
                color: getSubjectTypeColor(),
              }}
            />
          </Box> */}
        </Box>
        <IconButton onClick={onClose} className="subject-instructor-close-btn" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider className="subject-instructor-divider" />

      <DialogContent className="subject-instructor-content">
        {loading ? (
          <Box className="subject-instructor-loading">
            <CircularProgress style={{ color: getSubjectTypeColor() }} />
            <Typography variant="body2" className="loading-text">
              Loading subjects...
            </Typography>
          </Box>
        ) : error ? (
          <Box className="subject-instructor-error">
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        ) : (
          <Box className="subject-instructor-list-container">
            <Typography variant="subtitle2" className="subject-list-header">
              Subjects by Instructors Count
            </Typography>

            <Grid container spacing={3} className="subject-instructor-grid">
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                <Box className="subject-column">
                  {leftColumn.map((subject, index) => (
                    <Card key={subject.id} className="subject-instructor-card">
                      <CardContent className="subject-card-content">
                        <Box className="subject-card-header">
                          <Box className="subject-rank">
                            <Typography variant="body2" className="rank-number">
                              #{index + 1}
                            </Typography>
                          </Box>
                          <Typography variant="body1" className="subject-title-text">
                            {subject.title}
                          </Typography>
                          <Chip
                            label={`${subject.instructor_count} Instructors`}
                            className="instructor-count-item-chip"
                            style={{
                              backgroundColor: getSubjectTypeColor() + '15',
                              color: getSubjectTypeColor(),
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6}>
                <Box className="subject-column">
                  {rightColumn.map((subject, index) => (
                    <Card key={subject.id} className="subject-instructor-card">
                      <CardContent className="subject-card-content">
                        <Box className="subject-card-header">
                          <Box className="subject-rank">
                            <Typography variant="body2" className="rank-number">
                              #{midPoint + index + 1}
                            </Typography>
                          </Box>
                          <Typography variant="body1" className="subject-title-text">
                            {subject.title}
                          </Typography>
                          <Chip
                            label={`${subject.instructor_count} Instructors`}
                            className="instructor-count-item-chip"
                            style={{
                              backgroundColor: getSubjectTypeColor() + '15',
                              color: getSubjectTypeColor(),
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubjectInstructor;
